import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getISOWeek,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import type { LensPeriod } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import type { MatrixCut } from '../matrix/matrixState'

export type DayColumn = {
  index: number
  date: Date
  iso: string
  weekdayShort: string
  dayLabel: string
  isOutsideMonth: boolean
  isWeekend: boolean
  isToday: boolean
  /** Domingo: fecha a semana (separador no layout Contínuo). */
  isWeekEnd: boolean
}

export type WeekBlock = {
  number: number
  startIndex: number
  days: DayColumn[]
  rangeLabel: string
  isCurrent: boolean
  outsideNote: string | null
}

const WEEK_OPTIONS = { weekStartsOn: 1 as const }
const WEEKDAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
const pad = (n: number) => String(n).padStart(2, '0')
const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1)

export function getMonthGridRange(anchor: Date) {
  const monthStart = startOfMonth(anchor)
  const monthEnd = endOfMonth(anchor)
  return {
    start: startOfWeek(monthStart, WEEK_OPTIONS),
    end: endOfWeek(monthEnd, WEEK_OPTIONS),
    monthStart,
    monthEnd
  }
}

export function buildDayColumns(
  start: Date,
  end: Date,
  options: { month: Date | null; today: Date }
): DayColumn[] {
  const total = differenceInCalendarDays(end, start) + 1
  return Array.from({ length: total }, (_, index) => {
    const date = addDays(start, index)
    const weekdayIndex = (date.getDay() + 6) % 7
    return {
      index,
      date,
      iso: format(date, 'yyyy-MM-dd'),
      weekdayShort: WEEKDAY_SHORT[weekdayIndex],
      dayLabel: format(date, 'dd/MM'),
      isOutsideMonth: options.month ? !isSameMonth(date, options.month) : false,
      isWeekend: weekdayIndex >= 5,
      isToday: isSameDay(date, options.today),
      isWeekEnd: weekdayIndex === 6
    }
  })
}

function outsideNote(days: DayColumn[]): string | null {
  const outside = days.filter((day) => day.isOutsideMonth)
  if (!outside.length) return null
  const monthName = format(outside[0].date, 'MMMM', { locale: ptBR })
  if (outside.length === 1) return `${outside[0].dayLabel} é de ${monthName}`
  return `${outside[0].dayLabel} a ${outside[outside.length - 1].dayLabel} são de ${monthName}`
}

/** "14–20/set"; cruzando meses leva o mês nas duas pontas ("28/set–04/out"), sem ambiguidade. */
function weekRangeLabel(first: Date, last: Date): string {
  const month = (date: Date) => format(date, 'MMM', { locale: ptBR })
  if (isSameMonth(first, last))
    return `${pad(first.getDate())}–${pad(last.getDate())}/${month(last)}`
  return `${pad(first.getDate())}/${month(first)}–${pad(last.getDate())}/${month(last)}`
}

export function splitIntoWeeks(days: DayColumn[]): WeekBlock[] {
  const weeks: WeekBlock[] = []
  for (let startIndex = 0; startIndex < days.length; startIndex += 7) {
    const slice = days.slice(startIndex, startIndex + 7)
    const inMonth = slice.filter((day) => !day.isOutsideMonth)
    const first = inMonth[0] ?? slice[0]
    const last = inMonth[inMonth.length - 1] ?? slice[slice.length - 1]
    weeks.push({
      number: weeks.length + 1,
      startIndex,
      days: slice,
      rangeLabel: weekRangeLabel(first.date, last.date),
      isCurrent: slice.some((day) => day.isToday),
      outsideNote: outsideNote(slice)
    })
  }
  return weeks
}

/**
 * Período que a navegação usa: a Matriz anda pelo recorte (`matriz`+`semana` se comporta
 * como `semana`; `matriz`+`mes` como `mes`). Os demais períodos passam direto.
 */
export function resolveNavPeriod(period: LensPeriod, cut: MatrixCut = 'mes'): LensPeriod {
  return period === 'matriz' ? cut : period
}

export function getPeriodRange(
  period: LensPeriod,
  anchor: Date,
  cut?: MatrixCut
): { startDate: Date; endDate: Date } {
  // Timeline (Onda 2.3): o próprio dia, 00:00 → 23:59:59.999 locais.
  if (period === 'timeline') return { startDate: startOfDay(anchor), endDate: endOfDay(anchor) }
  if (resolveNavPeriod(period, cut) === 'semana') {
    return {
      startDate: startOfWeek(anchor, WEEK_OPTIONS),
      endDate: endOfWeek(anchor, WEEK_OPTIONS)
    }
  }
  const { start, end } = getMonthGridRange(anchor)
  return { startDate: start, endDate: end }
}

/** O início da grade do mês fica no máximo 6 dias antes do dia 1: +6 cai sempre dentro do mês. */
export function anchorFromRangeStart(period: LensPeriod, start: Date, cut?: MatrixCut): Date {
  if (period === 'timeline') return start
  return resolveNavPeriod(period, cut) === 'semana' ? start : addDays(start, 6)
}

export function shiftAnchor(
  period: LensPeriod,
  anchor: Date,
  direction: 1 | -1,
  cut?: MatrixCut
): Date {
  if (period === 'timeline') return addDays(anchor, direction)
  if (resolveNavPeriod(period, cut) === 'semana') return addWeeks(anchor, direction)
  return addMonths(startOfMonth(anchor), direction)
}

/** "Quinta, 17 de setembro de 2026": dia da semana sem o "-feira" (spec Onda 2.3 §5.2). */
export function formatTimelineDayLabel(day: Date): string {
  const weekday = format(day, 'EEEE', { locale: ptBR }).replace('-feira', '')
  return `${capitalize(weekday)}, ${format(day, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}`
}

export function formatPeriodLabel(period: LensPeriod, anchor: Date, cut?: MatrixCut): string {
  if (period === 'timeline') return formatTimelineDayLabel(anchor)
  if (resolveNavPeriod(period, cut) !== 'semana') {
    return capitalize(format(anchor, "MMMM 'de' yyyy", { locale: ptBR }))
  }
  const { startDate, endDate } = getPeriodRange('semana', anchor)
  const dayMonth = (date: Date) => format(date, "d 'de' MMMM", { locale: ptBR })
  if (startDate.getFullYear() !== endDate.getFullYear()) {
    return `${dayMonth(startDate)} de ${startDate.getFullYear()} – ${dayMonth(endDate)} de ${endDate.getFullYear()}`
  }
  if (startDate.getMonth() !== endDate.getMonth()) {
    return `${dayMonth(startDate)} – ${dayMonth(endDate)} de ${endDate.getFullYear()}`
  }
  return `${startDate.getDate()} – ${dayMonth(endDate)} de ${endDate.getFullYear()}`
}

export function formatPeriodSubLabel(period: LensPeriod, anchor: Date, cut?: MatrixCut): string {
  // Timeline: o rótulo já diz o dia inteiro; o "Hoje" é botão da barra da Timeline.
  if (period === 'timeline') return ''
  if (resolveNavPeriod(period, cut) === 'semana') {
    return `Semana ${getISOWeek(getPeriodRange('semana', anchor).startDate)}`
  }
  const { start, end, monthStart, monthEnd } = getMonthGridRange(anchor)
  const weeks = Math.round((differenceInCalendarDays(end, start) + 1) / 7)
  return `${pad(monthStart.getDate())}–${pad(monthEnd.getDate())}/${pad(monthEnd.getMonth() + 1)} · ${weeks} semanas`
}
